// Pure helpers for the Project Datasheet Composer's Index Sheet Datasheets tab. No React, no API.
import { parseDsIndex } from '../development/dsIndexName';
import { isTempId, boxToLine, lineKey } from './dsBoxes';

// The server's SheetState names → what the tab says about them.
export const STATE_LABEL = {
  None: 'No datasheet',
  Assigned: 'Assigned',
  Stale: 'Stale',
  MissingInProject: 'Missing in project',
  Missing: 'Missing',
  LegacyKey: 'Legacy key',
  NotAssignable: 'Not allowed',
  NotInLibrary: 'Not in library',
};

// State → badge colour. One helper per page (ui-standards); unknown values fall back to gray.
const STATE_BADGE = {
  None: 'stui-badge-gray',
  Assigned: 'stui-badge-green',
  Stale: 'stui-badge-amber',
  MissingInProject: 'stui-badge-orange',
  Missing: 'stui-badge-red',
  LegacyKey: 'stui-badge-purple',
  NotAssignable: 'stui-badge-red',
  NotInLibrary: 'stui-badge-orange',
};
export const stateBadgeClass = state => STATE_BADGE[state] ?? 'stui-badge-gray';
export const stateLabel = state => STATE_LABEL[state] ?? String(state ?? '');

// States that need someone to act on them.
const ATTENTION = new Set(['Stale', 'MissingInProject', 'Missing', 'LegacyKey', 'NotAssignable', 'NotInLibrary']);
export const needsAttention = row => ATTENTION.has(row?.state);

// The rev from a DS_Index key (the text after the last underscore), or '' for none.
export const revOf = key => {
  const k = String(key ?? '');
  const i = k.lastIndexOf('_');
  return i < 0 ? '' : k.slice(i + 1);
};

// What can be done to one row, in the order the buttons show. A non-conforming code gets nothing.
//   create      - no datasheet yet: start one from a Published ISA Template
//   useExisting - no datasheet yet, but the library holds a copy carrying this code
//   assign      - has a datasheet and another revision of it to move to
//   revise      - on its own, current, identical copy: make the next revision
//   editLayout  - has a datasheet in this project: edit its boxes (layoutBlocker says when it is refused)
//   diff        - stale copy: open the difference viewer (refresh or keep from there)
//   bringIn     - the library has the copy but this project does not
export function rowActions(row) {
  if (!row || !row.conforming) return [];
  const hasCandidates = (row.candidates?.length ?? 0) > 0;
  const out = [];
  if (row.state === 'None') {
    out.push('create');
    if (hasCandidates) out.push('useExisting');
    return out;
  }
  if (row.state === 'Stale') out.push('diff');
  if (row.state === 'MissingInProject') out.push('bringIn');
  if (row.state === 'Assigned') out.push('revise');
  if (row.dsConfig != null && (row.state === 'Assigned' || row.state === 'Stale')) out.push('editLayout');
  if (hasCandidates) out.push('assign');
  return out;
}

// Rows per filter, for the filter picker's counts.
export const FILTERS = [
  ['all', 'All'],
  ['attention', 'Needs attention'],
  ['none', 'No datasheet'],
  ['assigned', 'Assigned'],
];

export function filterRows(rows, filter) {
  const list = rows ?? [];
  switch (filter) {
    case 'attention': return list.filter(needsAttention);
    case 'none': return list.filter(r => r.state === 'None');
    case 'assigned': return list.filter(r => r.state === 'Assigned');
    default: return list;
  }
}

// The ISA Template picker's option text: form, description, and the category it belongs to.
export const templateLabel = t =>
  [t?.dsName, t?.desc].filter(Boolean).join(' — ') + (t?.category ? ` (${t.category})` : '');

// The text to show for a failed call: the server's own message when it sent one (a 400 or 409
// here always explains itself), the access-denied text on a 403, otherwise the fallback.
export function errText(e, fallback) {
  const status = e?.response?.status;
  if (status === 403) return 'Access denied. You do not have permission to use the Project Datasheet Composer.';
  return e?.response?.data?.error || fallback;
}

// Why a row's datasheet layout cannot be edited, or null when it can. Mirrors the server's rules
// (DsProjectSheets.LayoutBlocker), which stay the authority: this covers what the row itself shows,
// then falls back to the server's own verdict for what only the server can see (another database,
// another Index Sheet or the catalog using the revision, a database that could not be read).
export function layoutBlocker(row) {
  if (!row?.conforming) return 'Rename the Index Sheet to the code grammar first.';
  if (row.dsConfig == null) return 'This Index Sheet has no datasheet yet.';
  const parts = parseDsIndex(row.dsIndex);
  if (!parts) return `${row.dsIndex} is a legacy key. Only re-keyed datasheets can be edited here.`;
  if (parts.isPlain) return `${row.dsIndex} is an ISA Template. A layout is edited on an Index Sheet Datasheet revision, never on an ISA Template.`;
  if (row.state === 'NotAssignable') return row.stateNote || `${row.dsIndex} is not this Index Sheet's own datasheet.`;
  if (row.published) return `${row.dsIndex} is Published (promoted to the catalog) and cannot be edited. Revise it and edit the new revision.`;
  if (row.state === 'Stale') return "This project's copy differs from the library. Refresh it from the library before editing its layout.";
  if (row.state !== 'Assigned') return row.stateNote || 'This datasheet is not available to edit in this project.';
  return row.layoutBlocker ?? null;
}

// The save request for the boxes on the canvas, diffed against what was loaded. `baseline` maps a
// real box id to the lineKey it had when loaded, so an untouched box is never sent and keeps its
// own DS_RevUser/DS_RevDate. New boxes carry a temp id and are created; a loaded box missing from
// the canvas is removed. Returns { creates, updates, deletes }.
export function layoutChanges(boxes, baseline) {
  const creates = [];
  const updates = [];
  const kept = new Set();
  for (const box of boxes ?? []) {
    const { dsIndexId, ...line } = boxToLine(box, null);   // eslint-disable-line no-unused-vars
    if (isTempId(box.id)) { creates.push(line); continue; }
    kept.add(box.id);
    if (baseline.get(box.id) !== lineKey(line)) updates.push({ id: box.id, ...line });
  }
  const deletes = [...baseline.keys()].filter(id => !kept.has(id));
  return { creates, updates, deletes };
}
